import React, { useState } from "react"
import { useSelector } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { DANGER, VIEW } from "const"
import { hasProjectAccess } from "client/utils"
import {
    PRM_SIL_RESULTS_PDF,
    PRM_CC_RESULTS_PDF,
    StoreType,
    ProjectActiveModules,
    moduleIds,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageForm,
    FormGroup,
    CheckButton,
    CheckboxWrapper,
    Feedback,
    Button,
    SpinnerIcon,

    LangReportWrapper,
    LangReportSwitcher,
} from "client/components"

import ExportSilPDF from "client/pages/sil/results/exportToPDF"
import ComplianceExportToPDF from "client/pages/cc/exportToPDF/compliance"
import ReviewComplianceExportToPDF from "client/pages/cc/exportToPDF/review"
import RequirementExportToPDF from "client/pages/cc/exportToPDF/requirement"

const Reporting = () => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const projectSettings = useSelector((state: StoreType) => state.project.projectSettings)

    const moduleList = useSelector((state: StoreType) => state.module.moduleList)
    const userList = useSelector((state: StoreType) => state.user.userList)
    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const userSettings = useSelector((state: StoreType) => state.system.userSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const langReport = useSelector((state: StoreType) => state.system.langReport)

    const [silPdf, setSilPdf] = useState(false)
    const [ccCompliancePdf, setCcCompliancePdf] = useState(false)
    const [ccReviewCompliancePdf, setCcReviewCompliancePdf] = useState(false)
    const [ccRequirementsPdf, setCcRequirementsPdf] = useState(false)

    const [silInProgress, setSilInProgress] = useState(false)
    const [complianceInProgress, setComplianceInProgress] = useState(false)
    const [reviewComplianceInProgress, setReviewComplianceInProgress] = useState(false)
    const [requirementsInProgress, setRequirementsInProgress] = useState(false)

    const projectActiveModules: ProjectActiveModules = project.modules ? JSON.parse(project.modules) : {}

    const printReports = () => {
        if (silPdf) {
            ExportSilPDF(project, moduleList, accountSettings, projectSettings, userSettings, userData, langReport, setSilInProgress)
        }
        if (ccCompliancePdf) {
            ComplianceExportToPDF(null, project, moduleList, userList, userSettings, userData, langReport, setComplianceInProgress)
        }
        if (ccReviewCompliancePdf) {
            ReviewComplianceExportToPDF(
                null,
                null,
                project,
                moduleList,
                userList,
                userSettings,
                userData,
                langReport,
                setReviewComplianceInProgress,
            )
        }
        if (ccRequirementsPdf) {
            RequirementExportToPDF(null, project, moduleList, userList, userSettings, userData, langReport, setRequirementsInProgress)
        }
    }

    const selectAll = (allSelected: boolean) => {
        if (allSelected) {
            setSilPdf(false)
            setCcCompliancePdf(false)
            setCcReviewCompliancePdf(false)
            setCcRequirementsPdf(false)
        } else {
            if (projectActiveModules[moduleIds.sil]) setSilPdf(true)
            if (projectActiveModules[moduleIds.cc]) {
                setCcCompliancePdf(true)
                setCcReviewCompliancePdf(true)
                setCcRequirementsPdf(true)
            }
        }
    }

    const silPdfAccess = hasProjectAccess(PRM_SIL_RESULTS_PDF, VIEW)
    const ccPdfAccess = hasProjectAccess(PRM_CC_RESULTS_PDF, VIEW)

    const allSelected = (projectActiveModules[moduleIds.sil] ? silPdf : true)
        && (
            projectActiveModules[moduleIds.cc]
                ? ccCompliancePdf && ccReviewCompliancePdf && ccRequirementsPdf
                : true
        )
    const exportInProgress = silInProgress
        || complianceInProgress
        || reviewComplianceInProgress
        || requirementsInProgress

    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.reporting}</PageTitle>
                <LangReportWrapper>
                    <LangReportSwitcher />
                </LangReportWrapper>
            </PageHeader>
            <PageContent>
                <PageForm>
                    <Row>
                        <Col xs={12}>
                            <p>{lang.selectModulesForReport}</p>

                            <FormGroup>
                                <CheckboxWrapper>
                                    <Col xs={12} md={4}>
                                        {
                                            projectActiveModules[moduleIds.sil] && (
                                                <CheckButton
                                                    checked={silPdf}
                                                    readOnly={!silPdfAccess}
                                                    onChange={() => setSilPdf(!silPdf)}
                                                    title={moduleList[moduleIds.sil].name}
                                                />
                                            )
                                        }
                                        {
                                            projectActiveModules[moduleIds.cc] && ([
                                                <CheckButton
                                                    key="compliance"
                                                    checked={ccCompliancePdf}
                                                    readOnly={!ccPdfAccess}
                                                    onChange={() => setCcCompliancePdf(!ccCompliancePdf)}
                                                    title={lang.compliance}
                                                />,
                                                <CheckButton
                                                    key="reviewCompliance"
                                                    checked={ccReviewCompliancePdf}
                                                    readOnly={!ccPdfAccess}
                                                    onChange={() => setCcReviewCompliancePdf(!ccReviewCompliancePdf)}
                                                    title={lang.reviewCompliance}
                                                />,
                                                <CheckButton
                                                    key="requirementsReview"
                                                    checked={ccRequirementsPdf}
                                                    readOnly={!ccPdfAccess}
                                                    onChange={() => setCcRequirementsPdf(!ccRequirementsPdf)}
                                                    title={lang.requirementsReview}
                                                />,
                                            ])
                                        }
                                    </Col>
                                </CheckboxWrapper>
                            </FormGroup>

                            <FormGroup>
                                <Feedback variant={DANGER}>{lang.reportNotificationMain}</Feedback>
                            </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <Button
                                row
                                disabled={exportInProgress}
                                onClick={() => selectAll(allSelected)}
                            >
                                { allSelected ? lang.deselectAll : lang.selectAll }
                            </Button>

                            <Button
                                row
                                variant="success"
                                disabled={exportInProgress}
                                onClick={printReports}
                            >
                                {
                                    exportInProgress
                                        ? <SpinnerIcon />
                                        : lang.export
                                }
                            </Button>
                        </Col>
                    </Row>
                </PageForm>
            </PageContent>
        </Container>
    )
}
export default Reporting
